import React from "react"
import {useEffect} from "react";
import {Modal, Form, Input, message, Button, Popconfirm} from "antd";
import dayjs from "dayjs";
import {useTranslation} from "react-i18next";

const NoteModal = ({
                       open, date, onClose, onSuccess,
                       fetchWrapper, baseUrl,
                       mode = "create",
                       initialNote,
                       onDeleted,
                   }) => {
    const {t} = useTranslation();
    const [form] = Form.useForm();

    useEffect(() => {
        if (!open) return;
        form.resetFields();
        if (mode === "edit" && initialNote) {
            form.setFieldsValue({
                title: initialNote.title || "",
                description: initialNote.description || "",
            });
        } else {
            form.setFieldsValue({title: "", description: ""});
        }
    }, [open, form, mode, initialNote]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (mode === "edit" && initialNote?.id) {
                const updatedNote = await fetchWrapper.put(`${baseUrl}/notes/update/${initialNote.id}`, {
                    title: String(values.title).trim(),
                    description: String(values.description || ""),
                });
                message.success(t("publicationCalendar.noteModal.messages.updated"));
                onSuccess?.(updatedNote);
            } else {
                const dateStr = dayjs(date).format("YYYY-MM-DD");
                const createdNote = await fetchWrapper.post(`${baseUrl}/notes/create`, {
                    title: String(values.title).trim(),
                    description: String(values.description || ""),
                    date: dateStr,
                })
                message.success(t("publicationCalendar.noteModal.messages.success"));
                onSuccess?.(createdNote);
            }
            onClose?.();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(t("publicationCalendar.noteModal.messages.failed"));
        }
    }

    const handleDelete = async () => {
        if (!(mode === "edit" && initialNote?.id)) return;
        try {
            await fetchWrapper.delete(`${baseUrl}/notes/remove/${initialNote.id}`);
            message.success(t("publicationCalendar.noteModal.messages.delete"));
            onDeleted?.(initialNote);
            onClose?.();
        } catch (e) {
            message.error(t("publicationCalendar.noteModal.errors.deleteNote"));
        }
    };

    return (
        <Modal
            title={mode === "edit" ? t("publicationCalendar.noteModal.titleEdit") : t("publicationCalendar.noteModal.title")}
            open={open}
            onCancel={onClose}
            destroyOnClose
            footer={[
                ...(mode === "edit" ? [
                    <Popconfirm
                        key="delete"
                        title={t("publicationCalendar.noteModal.deleteCards.title")}
                        description={t("publicationCalendar.noteModal.deleteCards.description")}
                        okText={t("publicationCalendar.noteModal.buttons.delete")}
                        cancelText={t("publicationCalendar.noteModal.buttons.cancel")}
                        okButtonProps={{danger: true}}
                        onConfirm={handleDelete}
                    >
                        <Button danger>
                            {t("publicationCalendar.noteModal.buttons.delete")}
                        </Button>
                    </Popconfirm>
                ] : []),
                <Button key="cancel" onClick={onClose}>
                    {t("publicationCalendar.noteModal.buttons.cancel")}
                </Button>,
                <Button key="ok" type="primary" onClick={handleSave}>
                    {mode === "edit"
                        ? t("publicationCalendar.noteModal.buttons.save")
                        : t("publicationCalendar.noteModal.buttons.create")}
                </Button>,
            ]}
        >
            {mode !== "edit" && (
                <div className="text-xs text-gray-500 mb-2">
                    {t("publicationCalendar.noteModal.fields.selectedDate")}
                    <strong>{dayjs(date).format("YYYY-MM-DD")}</strong>
                </div>
            )}
            <Form layout="vertical" form={form}>
                <Form.Item
                    name="title"
                    label={t("publicationCalendar.noteModal.fields.title")}
                    rules={[{required: true, message: t("publicationCalendar.noteModal.errors.titleRequired")}]}
                >
                    <Input placeholder={t("publicationCalendar.noteModal.placeholders.title")}/>
                </Form.Item>
                <Form.Item
                    name="description"
                    label={t("publicationCalendar.noteModal.fields.description")}
                >
                    <Input.TextArea rows={4} placeholder={t("publicationCalendar.noteModal.placeholders.description")}/>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default NoteModal;